import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { z } from "zod";
import { authService } from "../../services/auth.service";
import { Mail, ArrowLeft, CheckCircle, ArrowRight } from "lucide-react";
import logo from "../../assets/logo.png";

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    setError("");
    setSuccess(false);
    setIsLoading(true);

    try {
      await authService.forgotPassword(data.email);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        "Failed to send reset email. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white overflow-hidden">
      {/* Left Side - Immersive Hero Section with Blue/Teal Branding */}
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden bg-blue-600 text-white rounded-tr-[50px] rounded-br-[50px]">
        {/* Elite Aurora Background - Blue/Teal Theme */}

        {/* Elite Aurora Background - Blue/Teal Theme */}
        <div className="absolute inset-0 z-0 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600" />
          <div className="absolute top-[-10%] left-[-10%] w-[100%] h-[100%] rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[80%] h-[80%] rounded-full bg-white/10 blur-[100px] animate-pulse delay-700" />
          <div className="absolute top-[20%] left-[10%] w-[60%] h-[60%] rounded-full bg-blue-400/20 blur-[110px] animate-pulse delay-1000" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
        </div>

        {/* Branding - Elite Logo Pill */}
        <div className="relative z-10">
          <div className="flex items-center gap-3.5 bg-white backdrop-blur-2xl py-3 px-6 rounded-2xl border border-white/20 shadow-2xl w-fit group hover:bg-white/20 transition-all duration-300">
            <div className="flex flex-col text-white">
              <span className="text-5xl font-black tracking-tight leading-none">
                {/* MediCorePay */}
                <span className="text-blue-800">Medi</span>
                <span className="text-red-700">Core</span>
                <span className="text-teal-700">Pay</span>
              </span>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg space-y-10">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase">
              <span className="flex h-1.5 w-1.5 rounded-full bg-teal-300 animate-ping" />
              Recovery Mode Active
            </div>
            <h1 className="text-6xl font-black leading-[1.1] tracking-tighter text-white">
              Forgot Your <br />
              <span className="text-teal-200">Credential?</span>
            </h1>
            <p className="text-xl text-blue-50 font-light leading-relaxed max-w-md">
              Security is our core priority. We'll help you securely re-verify your identity and restore access to your pharmacy dashboard.
            </p>
          </div>

          <div className="flex items-center gap-5 p-5 rounded-[32px] w-fit border-white/10 bg-white/10 backdrop-blur-md">
            <div className="w-12 h-12 rounded-2xl bg-teal-400 flex items-center justify-center shadow-lg shadow-teal-400/20">
              <CheckCircle className="w-6 h-6 text-white stroke-[3]" />
            </div>
            <div>
              <p className="text-sm font-black text-white">Encrypted Links</p>
              <p className="text-xs text-blue-100 font-bold">SHA-256 Protected Verification</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex justify-between items-end">
          <p className="text-sm text-blue-100 font-medium">© 2026 MediCorePay Global</p>
          <div className="flex gap-8">
            <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">Infosec</a>
            <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">Support</a>
          </div>
        </div>
      </div>

      {/* Right Side - Recovery Form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12 w-full animate-fade-in relative overflow-hidden">
        <div className="w-full max-w-[440px] space-y-12">
          {/* Logo above Title - X-Large format */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={logo}
              alt="MediCorePay Logo"
              className="w-25 h-25 object-contain max-w-[200px] mb-8 group hover:scale-105 transition-all duration-500"
            />
            <div className="space-y-4 text-center">
              <h2 className="text-5xl font-black text-slate-950 tracking-tighter leading-none">Recover Access</h2>
              <p className="text-slate-500 font-bold text-lg">
                Enter the email associated with your professional account.
              </p>
            </div>
          </div>

          {success ? (
            <div className="glass p-10 rounded-[40px] text-center space-y-8 animate-fade-in border-slate-100">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-emerald-400/20 blur-2xl rounded-full" />
                <div className="relative w-24 h-24 rounded-[32px] bg-emerald-500 flex items-center justify-center shadow-2xl shadow-emerald-500/20 mx-auto">
                  <Mail className="w-12 h-12 text-white stroke-[2.5]" />
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-3xl font-black text-slate-900 tracking-tight">Transmission Sent</h3>
                <p className="text-base text-slate-500 font-bold leading-relaxed px-4">
                  Verification telemetry has been dispatched to your inbox. Please check even your junk folder.
                </p>
              </div>
              <button
                onClick={() => setSuccess(false)}
                className="w-full py-5 bg-slate-950 text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-black transition-all shadow-xl shadow-slate-900/10 active:scale-95"
              >
                Resend Transmission
              </button>
            </div>
          ) : (
            <div className="glass p-10 rounded-[40px] border-slate-100 shadow-2xl shadow-indigo-500/5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-[11px] font-black uppercase tracking-wider flex items-center gap-3 animate-fade-in">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    {error}
                  </div>
                )}

                <div className="space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                    Email Repository
                  </label>
                  <div className="relative group">
                    <input
                      {...register("email")}
                      className="block w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500/20 focus:ring-4 focus:ring-indigo-500/5 transition-all duration-300 outline-none text-slate-950 placeholder-slate-400 font-bold"
                      placeholder="professional@pharmacy.com"
                    />
                    <div className="absolute inset-y-0 right-0 pr-6 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-500 transition-colors">
                      <Mail className="h-5 w-5 stroke-[2.5]" />
                    </div>
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1.5 animate-slide-up">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full relative group py-5 bg-slate-950 hover:bg-black text-white font-black text-[13px] uppercase tracking-[0.2em] rounded-2xl shadow-2xl shadow-slate-950/20 active:scale-[0.98] transition-all duration-300 overflow-hidden disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Synchronizing...
                      </>
                    ) : (
                      <>
                        Transmit Link
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform stroke-[3]" />
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-3 text-slate-400 font-black text-[11px] uppercase tracking-widest hover:text-slate-900 transition-all group active:scale-95"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform stroke-[3]" />
              Return to Authentication
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
